import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Drives a horizontally resizable panel (WhatsApp Web style).
 *
 * Returns the current width plus a pointer-down handler for the drag handle.
 * The width is clamped to [MIN, MAX] and persisted to localStorage at the
 * end of each drag.
 */
const MIN_WIDTH = 280;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 440;
const STORAGE_KEY = 'beworks:desktop:listWidth';

const clamp = (value) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, value));

const readStoredWidth = () => {
  if (typeof window === 'undefined' || !window.localStorage) return DEFAULT_WIDTH;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_WIDTH;
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) return DEFAULT_WIDTH;
    return clamp(parsed);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const writeStoredWidth = (width) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(width));
  } catch {
    /* storage unavailable (private mode) — ignore */
  }
};

export const useResizableWidth = () => {
  const [width, setWidth] = useState(readStoredWidth);
  const [isDragging, setIsDragging] = useState(false);

  // Live drag refs — avoid stale closures inside window listeners.
  const startXRef = useRef(0);
  const startWidthRef = useRef(width);
  const widthRef = useRef(width);
  widthRef.current = width;

  const handleMove = useCallback((e) => {
    const next = clamp(startWidthRef.current + (e.clientX - startXRef.current));
    setWidth(next);
  }, []);

  const handleUp = useCallback(() => {
    window.removeEventListener('pointermove', handleMove);
    window.removeEventListener('pointerup', handleUp);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    setIsDragging(false);
    writeStoredWidth(widthRef.current);
  }, [handleMove]);

  const startDrag = useCallback((e) => {
    startXRef.current = e.clientX;
    startWidthRef.current = widthRef.current;
    setIsDragging(true);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  }, [handleMove, handleUp]);

  // Cleanup if the component unmounts mid-drag.
  useEffect(() => () => {
    window.removeEventListener('pointermove', handleMove);
    window.removeEventListener('pointerup', handleUp);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, [handleMove, handleUp]);

  return { width, isDragging, startDrag };
};
